import type { User, UserSettings } from '@shared/models';
import { defineCacheStore, type CacheLoadFn } from '@shared/stores/cache';
import type { User as FirebaseUser } from 'firebase/auth';
import { defineStore, storeToRefs } from 'pinia';
import { computed, type Ref } from 'vue';

async function loadDataFromDb() {
  const start = new Date();
  console.log(`[store.auth] data loading: ${start}`);
  const { getCurrentUser: getCurrentFirebaseUser } = await import('../helpers/auth');
  const { getCurrentUser } = await import('@/helpers/db/user');

  const auth = await getCurrentFirebaseUser().catch(() => null);
  console.log(`[store.auth] auth retrieved: ${new Date().getTime() - start.getTime()}ms`);
  const userCredential = auth;
  if (auth) {
    console.log(`[store.auth] load user and claims: ${new Date().getTime() - start.getTime()}ms`);
    const [user, claims] = await Promise.all([
      getCurrentUser(),
      auth.getIdTokenResult(true).then((idTokenResult) => idTokenResult.claims),
    ]);
    console.log(`[store.auth] user and claims retrieved: ${new Date().getTime() - start.getTime()}ms`);
    return {
      userCredential: userCredential,
      user,
      claims: claims || {},
    };
  } else {
    console.log(`[store.auth] no auth found: ${new Date().getTime() - start.getTime()}ms`);
    return {
      userCredential: userCredential,
      user: null,
      claims: {},
    };
  }
}

const useUserCacheStore = defineCacheStore<{
  userCredential: FirebaseUser | null;
  user: User | null;
  claims: Record<string, number | string | boolean | unknown | null>;
}>(
  'user-cache',
  {
    userCredential: null,
    user: null,
    claims: {},
  },
  loadDataFromDb,
  (data) => data.userCredential === null
);

export const useUserStore = defineStore(
  'user',
  (): {
    userCredential: Ref<FirebaseUser | null>;
    setUserCredential: (newUser: FirebaseUser | null) => void;
    clearUserCredential: () => void;

    user: Ref<User | null>;
    setUser: (newUser: User | null) => void;
    clearUser: () => void;
    setClaims: (newClaims: Record<string, number | string | boolean | null>) => void;
    setSettings: (settings: Partial<UserSettings>) => void;

    claims: Ref<Record<string, number | string | boolean | unknown | null>>;

    loadAndUpdateCache: CacheLoadFn<{
      userCredential: FirebaseUser | null;
      user: User | null;
      claims: Record<string, number | string | boolean | unknown | null>;
    }>;

    state: {
      loading: boolean;
      finished: boolean;
    };
  } => {
    const cacheStore = useUserCacheStore();
    const { cache } = storeToRefs(cacheStore);
    const { loadAndUpdateCache, state } = cacheStore;

    const userCredential = computed({
      get: () => cache.value.userCredential,
      set: (val: FirebaseUser | null) => {
        cache.value = { ...cache.value, userCredential: val };
      },
    });
    const user = computed({
      get: () => cache.value.user,
      set: (val: User | null) => {
        cache.value = { ...cache.value, user: val };
      },
    });
    const claims = computed({
      get: () => cache.value.claims,
      set: (val: Record<string, number | string | boolean | unknown | null>) => {
        cache.value = { ...cache.value, claims: val };
      },
    });

    function setUserCredential(newUser: FirebaseUser | null) {
      userCredential.value = newUser;
    }
    function clearUserCredential() {
      userCredential.value = null;
    }

    function setUser(newUser: User | null) {
      user.value = newUser;
    }
    function clearUser() {
      user.value = null;
    }

    function setClaims(newClaims: Record<string, number | string | boolean | null>) {
      claims.value = newClaims;
    }

    function setSettings(settings: Partial<UserSettings>) {
      if (!user.value) {
        return;
      }
      user.value = {
        ...user.value,
        settings: { ...user.value.settings, ...settings } as UserSettings,
      };
    }

    return {
      userCredential,
      setUserCredential,
      clearUserCredential,
      user,
      setUser,
      clearUser,
      setClaims,
      claims,
      loadAndUpdateCache,
      setSettings,
      state,
    };
  }
);
